import { Module } from 'vuex'
import { RootState } from '@/store'
import { Referral } from '@/api/model/referral'
import { actions } from '@/store/referrals/actions'
import { mutations } from '@/store/referrals/mutations'
import { getters } from '@/store/referrals/getters'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import {
    PipelinePhaseCategory,
    ReferralAge,
    ReferralCount,
    ReferralFilterDirection,
    ReferralView,
} from '@/api/types'
import { ReferralStats } from '@/api/model/referral-stats'
import { StatusReason } from '@/api/model/status-reason'
import { Source } from '@/api/model/source'


export interface SearchAlternatives {
    userIn: Referral[]
    userOut: Referral[]
    teamIn: Referral[]
    teamOut: Referral[]
}

export interface ReferralsStateFilters {
    view: ReferralView,
    direction: ReferralFilterDirection
    search: string,
    searchAlternatives: SearchAlternatives,
    priorityFilter: number[],
    typeFilter: string[],
    statusFilter: string,
    statusReasonFilter: string[],
    newStatusReasonFilter: StatusReason[],
    referredByFilter: User[],
    referredByBranchFilter: Branch[],
    assignedToFilter: User[],
    assignedToBranchFilter: Branch[],
    channelFilter: string[],
    newSourceFilter: Source[],
    scoreFilter: number[],
    dateSpecificFilter: Date,
    dateRangeFilter: string,
    ageFilter: ReferralAge,
    pipelines: Pipeline[],
    outgoingPipelines: Pipeline[],
    pipelineFilter: string,
    phaseFilter: PipelinePhase,
    phaseCategoryFilter: PipelinePhaseCategory,
}

export interface ReferralsState extends ReferralsStateFilters {
    loading: boolean,
    loadingAlternatives: boolean,
    error: string|null,
    referrals: Referral[],
    totalItems: number,
    totalPages: number,
    page: number,
    itemsPerPage: number,
    displayMode: string,
    orderField: string|null,
    orderDirection: string|null,
    referralCounts: ReferralCount,
    referralStats: ReferralStats,
    snapshot: {
        priorityFilter: number[],
        typeFilter: string[],
        statusReasonFilter: string[],
        referredByFilter: User[],
        referredByBranchFilter: Branch[],
        assignedToFilter: User[],
        assignedToBranchFilter: Branch[],
        channelFilter: string[],
        newSourceFilter: Source[],
        scoreFilter: number[],
        dateSpecificFilter: Date,
        dateRangeFilter: string,
        ageFilter: ReferralAge,
    },
}

const state: ReferralsState = {
    loading: false,
    loadingAlternatives: false,
    error: null,
    referrals: [],
    totalItems: null,
    totalPages: 0,
    page: 1,
    itemsPerPage: 10,
    displayMode: 'appointments',
    orderField: 'updatedAt',
    orderDirection: 'desc',
    priorityFilter: [],
    typeFilter: [],
    statusFilter: null,
    statusReasonFilter: [],
    newStatusReasonFilter: [],
    referredByFilter: [],
    referredByBranchFilter: [],
    assignedToFilter: [],
    assignedToBranchFilter: [],
    search: null,
    searchAlternatives: {
        userIn: [],
        userOut: [],
        teamIn: [],
        teamOut: [],
    },
    view: null,
    direction: null,
    channelFilter: [],
    newSourceFilter: [],
    scoreFilter: [],
    dateSpecificFilter: null,
    dateRangeFilter: null,
    ageFilter: null,
    pipelines: [],
    outgoingPipelines: [],
    pipelineFilter: null,
    phaseFilter: null,
    phaseCategoryFilter: null,
    referralCounts: {
        statusAll: 0,
        statusNew: 0,
        statusAppointment: 0,
        statusProposal: 0,
        statusOnHold: 0,
        statusPending: 0,
        statusUnreachable: 0,
        statusSuccessful: 0,
        statusProcessed: 0,
        statusClosed: 0,
        scorePositive: 0,
        scoreNeutral: 0,
        scoreNegative: 0,
        typeAppraisal: 0,
        typeEpa: 0,
        typeInformation: 0,
        typeMortgage: 0,
        typePurchase: 0,
        typePhoto: 0,
        typeSale: 0,
        typeSignPlacement: 0,
        typeTaxation: 0,
        typeTechnicalAppraisal: 0,
        typeNewBuild: 0,
        age90: 0,
        age180: 0,
        age365: 0,
    },
    referralStats: new ReferralStats(),
    snapshot: {
        priorityFilter: [],
        typeFilter: [],
        statusReasonFilter: [],
        referredByFilter: [],
        referredByBranchFilter: [],
        assignedToFilter: [],
        assignedToBranchFilter: [],
        channelFilter: [],
        newSourceFilter: [],
        scoreFilter: [],
        dateSpecificFilter: null,
        dateRangeFilter: null,
        ageFilter: null,
    },
}

export const ReferralsModule: Module<ReferralsState, RootState> = {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
